import { useState, useMemo } from 'react';
import SAR from '@/components/feature/SAR';
import SARIcon from '@/components/feature/SARIcon';

interface WithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
  totalRaised: number;
  platformFeeRate: number;
  platformFee: number;
  availableBalance: number;
  ownerName: string;
}

// Saudi IBAN validation
const validateSaudiIBAN = (raw: string): {
  valid: boolean;
  message: string;
  status: 'empty' | 'invalid' | 'valid' | 'partial';
} => {
  const clean = raw.replace(/\s/g, '').toUpperCase();
  if (clean.length === 0) return { valid: false, message: '', status: 'empty' };
  if (!clean.startsWith('SA')) return { valid: false, message: 'الآيبان السعودي يجب أن يبدأ بـ SA', status: 'invalid' };
  if (clean.length < 24) return { valid: false, message: `${clean.length} / 24 حرف — أكمل رقم الآيبان`, status: 'partial' };
  if (clean.length > 24) return { valid: false, message: 'رقم الآيبان يجب أن يكون 24 حرفاً بالضبط', status: 'invalid' };
  const checkDigits = clean.slice(2, 4);
  if (!/^\d{2}$/.test(checkDigits)) return { valid: false, message: 'رقمان التحقق (الموضع 3-4) يجب أن يكونا أرقاماً', status: 'invalid' };
  const bban = clean.slice(4);
  if (!/^[A-Z0-9]{20}$/.test(bban)) return { valid: false, message: 'الآيبان يحتوي على أحرف غير صالحة', status: 'invalid' };
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  const numeric = rearranged.split('').map((c) => {
    const code = c.charCodeAt(0);
    return code >= 65 ? String(code - 55) : c;
  }).join('');
  let remainder = 0;
  for (let i = 0; i < numeric.length; i += 9) {
    remainder = Number(String(remainder) + numeric.slice(i, i + 9)) % 97;
  }
  if (remainder !== 1) return { valid: false, message: 'رقم الآيبان غير صحيح — تحقق من الأرقام مجدداً', status: 'invalid' };
  return { valid: true, message: 'آيبان سعودي صحيح ✓', status: 'valid' };
};

const formatIBAN = (raw: string): string => {
  const clean = raw.replace(/\s/g, '').toUpperCase().slice(0, 24);
  return clean.match(/.{1,4}/g)?.join(' ') ?? clean;
};

type Step = 'form' | 'confirm' | 'success';

const WithdrawalModal = ({
  isOpen,
  onClose,
  totalRaised,
  platformFeeRate,
  platformFee,
  availableBalance,
  ownerName,
}: WithdrawalModalProps) => {
  const [step, setStep] = useState<Step>('form');
  const [iban, setIban] = useState('');
  const [amount, setAmount] = useState(String(availableBalance));
  const [isLoading, setIsLoading] = useState(false);

  const fmt = (n: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
  const feePercent = Math.round(platformFeeRate * 100);
  const amountNum = Number(amount) || 0;

  const ibanValidation = useMemo(() => validateSaudiIBAN(iban), [iban]);

  const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\s/g, '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    setIban(formatIBAN(raw));
  };

  const ibanBorderClass = {
    empty: 'border-stone-200 focus:border-emerald-400',
    partial: 'border-amber-300 focus:border-amber-400',
    invalid: 'border-red-300 focus:border-red-400',
    valid: 'border-emerald-400 focus:border-emerald-500',
  }[ibanValidation.status];

  const ibanMsgClass = {
    empty: 'text-stone-400',
    partial: 'text-amber-600',
    invalid: 'text-red-500',
    valid: 'text-emerald-600',
  }[ibanValidation.status];

  const ibanIcon = {
    empty: null,
    partial: <i className="ri-time-line text-amber-500 text-sm"></i>,
    invalid: <i className="ri-close-circle-line text-red-500 text-sm"></i>,
    valid: <i className="ri-checkbox-circle-line text-emerald-500 text-sm"></i>,
  }[ibanValidation.status];

  const canProceed = ibanValidation.valid && amountNum > 0 && amountNum <= availableBalance;

  const handleConfirm = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setStep('success');
    }, 1800);
  };

  const handleClose = () => {
    setStep('form');
    setIban('');
    setAmount(String(availableBalance));
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col">

        {/* ── STEP: FORM ── */}
        {step === 'form' && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-xl">
                  <i className="ri-bank-line text-emerald-600 text-base"></i>
                </div>
                <div>
                  <h2 className="text-base font-black text-stone-800">طلب سحب الأموال</h2>
                  <p className="text-xs text-stone-400">التحويل خلال 3-5 أيام عمل</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Body — scrollable */}
            <div className="px-6 py-4 space-y-4 overflow-y-auto">

              {/* Fee breakdown card */}
              <div className="bg-stone-50 rounded-xl divide-y divide-stone-100 border border-stone-100">
                <div className="flex justify-between items-center px-4 py-3">
                  <span className="text-xs text-stone-500">إجمالي التبرعات المحصّلة</span>
                  <span className="text-sm font-bold text-stone-800"><SAR amount={totalRaised} iconSize={12} /></span>
                </div>
                <div className="flex justify-between items-center px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-stone-500">رسوم إدارية للمنصة</span>
                    <span className="text-xs bg-stone-200 text-stone-600 font-bold px-1.5 py-0.5 rounded-full">{feePercent}%</span>
                  </div>
                  <span className="text-sm font-semibold text-red-500">- <SAR amount={platformFee} iconSize={12} /></span>
                </div>
                <div className="flex justify-between items-center px-4 py-3 bg-emerald-50/60 rounded-b-xl">
                  <span className="text-sm font-bold text-emerald-800">الصافي المتاح للسحب</span>
                  <span className="text-lg font-black text-emerald-700"><SAR amount={availableBalance} iconSize={12} /></span>
                </div>
              </div>

              {/* Info note */}
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                <i className="ri-information-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
                <p className="text-xs text-amber-700 leading-relaxed">
                  تُخصم الرسوم الإدارية ({feePercent}%) تلقائياً من إجمالي التبرعات وفق الشروط المتفق عليها عند إنشاء الحملة.
                </p>
              </div>

              {/* Amount input */}
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">
                  المبلغ المراد سحبه <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    min={100}
                    max={availableBalance}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-xl font-black text-stone-800 focus:outline-none focus:border-emerald-400 text-right pr-14"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 text-sm font-medium"><SARIcon size={14} /></span>
                </div>
                {/* Quick amounts */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {[availableBalance, Math.floor(availableBalance * 0.75), Math.floor(availableBalance * 0.5)].map((v, i) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setAmount(String(v))}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                        amount === String(v)
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-stone-50 text-stone-600 border-stone-200 hover:border-emerald-300'
                      }`}
                    >
                      {i === 0 ? 'الكل' : <SAR amount={v} iconSize={12} />}
                    </button>
                  ))}
                </div>
                {amountNum > availableBalance && (
                  <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    المبلغ يتجاوز الرصيد المتاح (<SAR amount={availableBalance} iconSize={12} />)
                  </p>
                )}
              </div>

              {/* IBAN */}
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1">
                  رقم الآيبان البنكي (IBAN) <span className="text-red-400">*</span>
                </label>
                <p className="text-xs text-stone-400 mb-2">
                  مثال: <span className="font-mono tracking-wider">SA03 8000 0000 6080 1016 7519</span>
                </p>
                <div className="relative">
                  <input
                    type="text"
                    value={iban}
                    onChange={handleIBANChange}
                    placeholder="SA00 0000 0000 0000 0000 0000"
                    maxLength={29}
                    dir="ltr"
                    autoComplete="off"
                    spellCheck={false}
                    className={`w-full border-2 rounded-xl px-4 py-3 text-sm text-stone-800 focus:outline-none font-mono tracking-wider placeholder-stone-300 transition-colors ${ibanBorderClass}`}
                  />
                  {ibanValidation.status !== 'empty' && (
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">{ibanIcon}</div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex items-center gap-1.5 min-h-[16px]">
                    {ibanValidation.status === 'empty' ? (
                      <>
                        <i className="ri-shield-check-line text-emerald-500 text-xs"></i>
                        <p className="text-xs text-stone-400">معلوماتك البنكية مشفرة وآمنة</p>
                      </>
                    ) : (
                      <>
                        {ibanIcon}
                        <p className={`text-xs font-medium ${ibanMsgClass}`}>{ibanValidation.message}</p>
                      </>
                    )}
                  </div>
                  {ibanValidation.status !== 'empty' && (
                    <span className={`text-xs font-mono ${ibanValidation.status === 'valid' ? 'text-emerald-500' : 'text-stone-400'}`}>
                      {iban.replace(/\s/g, '').length} / 24
                    </span>
                  )}
                </div>
              </div>

              {/* Bottom info */}
              <div className="flex flex-wrap gap-3 text-xs text-stone-400 bg-stone-50 rounded-xl p-3">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-stone-300"></i>
                  3-5 أيام عمل
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-shield-check-line text-emerald-400"></i>
                  تحويل آمن ومشفر
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-bank-card-line text-stone-300"></i>
                  بدون رسوم سحب إضافية
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 pt-3 flex gap-3 shrink-0 border-t border-stone-100">
              <button
                onClick={handleClose}
                className="flex-1 py-3 rounded-xl border border-stone-200 text-stone-600 text-sm font-semibold hover:bg-stone-50 cursor-pointer transition-colors whitespace-nowrap"
              >
                إلغاء
              </button>
              <button
                onClick={() => setStep('confirm')}
                disabled={!canProceed}
                className={`flex-1 py-3 rounded-xl text-sm font-bold transition-colors whitespace-nowrap ${
                  canProceed
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer'
                    : 'bg-stone-100 text-stone-400 cursor-not-allowed'
                }`}
              >
                مراجعة الطلب
              </button>
            </div>
          </>
        )}

        {/* ── STEP: CONFIRM ── */}
        {step === 'confirm' && (
          <>
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100 shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setStep('form')}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-500 cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
                <h2 className="text-base font-black text-stone-800">تأكيد طلب السحب</h2>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-6 py-5 space-y-4 overflow-y-auto">
              {/* Summary */}
              <div className="bg-stone-50 rounded-xl divide-y divide-stone-100 border border-stone-100">
                <div className="flex justify-between items-center px-4 py-3">
                  <span className="text-sm text-stone-500">المستفيد</span>
                  <span className="text-sm font-semibold text-stone-800">{ownerName}</span>
                </div>
                <div className="flex justify-between items-center px-4 py-3">
                  <span className="text-sm text-stone-500">رقم الآيبان</span>
                  <span className="text-xs font-mono text-stone-700 tracking-wider">{iban}</span>
                </div>
                <div className="flex justify-between items-center px-4 py-3">
                  <span className="text-sm text-stone-500">إجمالي التبرعات</span>
                  <span className="text-sm font-semibold text-stone-700"><SAR amount={totalRaised} iconSize={12} /></span>
                </div>
                <div className="flex justify-between items-center px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-stone-500">رسوم إدارية</span>
                    <span className="text-xs bg-stone-200 text-stone-600 font-bold px-1.5 py-0.5 rounded-full">{feePercent}%</span>
                  </div>
                  <span className="text-sm font-semibold text-red-500">- <SAR amount={platformFee} iconSize={12} /></span>
                </div>
                <div className="flex justify-between items-center px-4 py-3 bg-emerald-50/60 rounded-b-xl">
                  <span className="text-sm font-bold text-emerald-800">المبلغ المحوَّل</span>
                  <span className="text-lg font-black text-emerald-700"><SAR amount={amountNum} iconSize={12} /></span>
                </div>
              </div>

              <div className="flex justify-between items-center px-4 py-3 bg-stone-50 rounded-xl border border-stone-100">
                <span className="text-sm text-stone-500">وقت التحويل المتوقع</span>
                <span className="text-sm font-semibold text-stone-700">3-5 أيام عمل</span>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                <i className="ri-information-line text-amber-600 text-lg mt-0.5 shrink-0"></i>
                <p className="text-xs text-amber-700 leading-relaxed">
                  بعد تأكيد الطلب، سيتم مراجعته من فريق هِبة المالي وإرسال التحويل خلال 3-5 أيام عمل. ستصلك رسالة تأكيد على بريدك الإلكتروني.
                </p>
              </div>
            </div>

            <div className="px-6 pb-5 pt-3 flex gap-3 shrink-0 border-t border-stone-100">
              <button
                onClick={() => setStep('form')}
                className="flex-1 py-3 rounded-xl border border-stone-200 text-stone-600 text-sm font-semibold hover:bg-stone-50 cursor-pointer transition-colors whitespace-nowrap"
              >
                تعديل
              </button>
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold cursor-pointer transition-colors whitespace-nowrap flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    جاري الإرسال...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    تأكيد الطلب
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* ── STEP: SUCCESS ── */}
        {step === 'success' && (
          <div className="px-6 py-10 flex flex-col items-center text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-emerald-50 rounded-full mb-5">
              <i className="ri-checkbox-circle-line text-emerald-500 text-4xl"></i>
            </div>
            <h2 className="text-xl font-black text-stone-800 mb-2">تم إرسال الطلب!</h2>
            <p className="text-sm text-stone-500 leading-relaxed mb-1">
              طلب سحب <strong className="text-stone-700"><SAR amount={amountNum} iconSize={12} /></strong> قيد المراجعة.
            </p>
            <p className="text-xs text-stone-400 mb-6">
              سيصلك التحويل خلال 3-5 أيام عمل على الآيبان المسجل.
            </p>

            {/* Mini breakdown */}
            <div className="bg-stone-50 rounded-xl w-full divide-y divide-stone-100 mb-6 border border-stone-100 text-right">
              <div className="flex justify-between items-center px-4 py-2.5">
                <span className="text-xs text-stone-400">إجمالي التبرعات</span>
                <span className="text-xs font-semibold text-stone-700"><SAR amount={totalRaised} iconSize={12} /></span>
              </div>
              <div className="flex justify-between items-center px-4 py-2.5">
                <span className="text-xs text-stone-400">رسوم إدارية ({feePercent}%)</span>
                <span className="text-xs font-semibold text-red-400">- <SAR amount={platformFee} iconSize={12} /></span>
              </div>
              <div className="flex justify-between items-center px-4 py-2.5">
                <span className="text-xs font-bold text-emerald-700">المبلغ المحوَّل</span>
                <span className="text-sm font-black text-emerald-700"><SAR amount={amountNum} iconSize={12} /></span>
              </div>
              <div className="flex justify-between items-center px-4 py-2.5">
                <span className="text-xs text-stone-400">الآيبان</span>
                <span className="text-xs font-mono text-stone-600 tracking-wider">{iban}</span>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold cursor-pointer transition-colors whitespace-nowrap"
            >
              حسناً، شكراً
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WithdrawalModal;
